import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Send,
  IndianRupee,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from "../lib/api";

const SubmitQuote = () => {
  const { requestId, providerId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    estimatedCost: '',
    estimatedTime: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [requestId, providerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestRes, providerRes] = await Promise.all([
        api.get(`/service-requests/${requestId}`),
        api.get(`/service-providers/public/${providerId}`),
      ]);

      setRequest(requestRes.data);
      setProvider(providerRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(error?.message || 'Failed to load quote page — link may be invalid');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.estimatedCost.trim()) {
      toast.error('Please enter estimated cost');
      return;
    }
    
    if (!formData.estimatedTime.trim()) {
      toast.error('Please enter estimated time');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const quoteData = {
        serviceRequestId: requestId,
        providerId: providerId,
        ...formData
      };
      
      await api.post(`/quotes`, quoteData);
      
      toast.success('Quote submitted successfully! Customer will review and contact you if accepted.');
      navigate('/');
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!request || !provider) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Submit Your Quote</h1>
              <p className="text-xs text-gray-600">Provide your best estimate for the customer</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Provider Info */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="text-4xl">{provider.category === 'Plumber' ? '🔧' : provider.category === 'Electrician' ? '⚡' : provider.category === 'Painter' ? '🎨' : '🔨'}</div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{provider.name}</h2>
                  <Badge>{provider.category}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Service Request Details */}
          <Card>
            <CardHeader>
              <CardTitle>Service Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{request.problemTitle}</h3>
                <p className="text-gray-700">{request.problemDescription}</p>
              </div>

              {request.images && request.images.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Problem Images:</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {request.images.map((img, index) => (
                      <img key={index} src={img} alt={`Problem ${index + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-gray-500">Location</p>
                  <p className="font-medium text-gray-900">{request.location}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Urgency</p>
                  <Badge>{request.urgency === 'urgent' ? '🔴 Urgent' : request.urgency === 'normal' ? '🟡 Normal' : '🟢 Can Wait'}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Form */}
          <Card>
            <CardHeader>
              <CardTitle>Submit Your Quote</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <IndianRupee className="h-4 w-4 text-blue-600" />
                      Estimated Cost *
                    </label>
                    <Input 
                      name="estimatedCost" 
                      value={formData.estimatedCost} 
                      onChange={handleInputChange} 
                      placeholder="e.g., ₹2,500 - ₹3,000" 
                      required 
                    />
                    <p className="text-xs text-gray-500">Include material and labor cost</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      Estimated Time *
                    </label>
                    <Input 
                      name="estimatedTime" 
                      value={formData.estimatedTime} 
                      onChange={handleInputChange} 
                      placeholder="e.g., 2-3 hours, 1 day" 
                      required 
                    />
                    <p className="text-xs text-gray-500">How long will the work take?</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Detailed Quote Description *</label>
                  <Textarea 
                    name="description" 
                    value={formData.description} 
                    onChange={handleInputChange} 
                    placeholder="Explain what work you'll do, materials needed, warranty, etc."
                    rows={5}
                    required
                  />
                  <p className="text-xs text-gray-500">Be specific - this helps customer make decision</p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Quote Submission Guidelines</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Provide accurate cost and time estimates</li>
                      <li>Customer will compare your quote with others</li>
                      <li>If accepted, you'll be contacted at {provider.phone}</li>
                      <li>Be competitive but fair with pricing</li>
                    </ul>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  disabled={isSubmitting}
                >
                  <Send className="h-5 w-5 mr-2" />
                  {isSubmitting ? 'Submitting Quote...' : 'Submit Quote to Customer'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubmitQuote;
